<template>
  <div class="w-full flex-1-0-auto header-height-top-space">
    <div class="w-full pb-4 xl:pb-6">
      <div class="mb-8 md:px-6 lg:px-8 xl:px-20 xl:mb-12">
        <div class="container">
          <StepNavigation
            :step1="tr('pages.sales.steps.step1')"
            :step2="tr('pages.sales.steps.step2')"
            :step3="tr('pages.sales.steps.step3')"
            :active-step="2"
          />
        </div>
      </div>

      <div id="main-content" class="w-full viewport-gutter">
        <div class="container">
          <div class="text-sm xl:text-lg">
            <h1 class="pb-3 text-3xl font-bold leading-none xl:text-4xl font-family-portado">
              {{ tr('pages.sales.checkout.title') }}
            </h1>
          </div>
        </div>
      </div>
    </div>

    <form
      class="w-full pt-6 pb-16 viewport-gutter xl:pt-12 xl:pb-24"
      @submit.prevent="onSubmit"
    >
      <div
        v-if="isLoading && !hasErrors"
        class="flex flex-col items-center justify-center h-full text-sm text-center xl:text-lg"
      >
        <span class="text-center loading-text">
          {{ tr('pages.sales.checkout.loading.title' ) }}
        </span>

        <div id="lh-loading" class="relative flex-shrink-0 w-full h-3 mt-4 bullets-9">
          <div id="lh-loading-1" class="absolute top-0 w-3 h-3 lh-loading" />
          <div id="lh-loading-2" class="absolute w-3 h-3 lh-loading" />
          <div id="lh-loading-3" class="absolute w-3 h-3 lh-loading" />
          <div id="lh-loading-4" class="absolute w-3 h-3 lh-loading" />
          <div id="lh-loading-5" class="absolute w-3 h-3 lh-loading" />
          <div id="lh-loading-6" class="absolute w-3 h-3 lh-loading" />
          <div id="lh-loading-7" class="absolute w-3 h-3 lh-loading" />
          <div id="lh-loading-8" class="absolute w-3 h-3 lh-loading" />
          <div id="lh-loading-9" class="absolute w-3 h-3 lh-loading" />
        </div>
        <p class="max-w-sm pt-6 mx-auto text-center xl:pt-12">
          {{ tr('pages.sales.checkout.loading.description' ) }}
        </p>
      </div>

      <div
        v-show="!isLoading"
        class="container xl:flex xl:justify-between"
      >
        <div class="w-full space-y-6 xl:max-w-209 xl:mr-5 md:space-y-10">
          <div id="payment-personal-data" class="w-full p-6 bg-au_gray-100">
            <p class="pb-4 font-bold md:text-2xl md:pb-6 font-family-portado">
              {{ tr('pages.sales.checkout.form.personal.title' ) }}
            </p>

            <div class="space-y-4 xl:space-y-6">
              <div class="flex flex-col space-y-4 md:flex-row md:justify-between md:space-y-0">
                <TextInput
                  v-model.trim="formFields.billingFirstName"
                  name="first-name"
                  :label="tr('components.flightVoucher.form.firstName.name')"
                  :placeholder="tr('components.flightVoucher.form.firstName.placeholder')"
                  :error="$v.formFields.billingFirstName.$error ? tr('errors.type.required') : ''"
                  extra-class="w-full md:w-1/2 md:mr-10 xl:mr-6"
                  variant="modern"
                  autocomplete="given-name"
                  @change="updateDefaults()"
                />
                <TextInput
                  v-model.trim="formFields.billingLastName"
                  name="last-name"
                  :label="tr('components.flightVoucher.form.lastName.name')"
                  :placeholder="tr('components.flightVoucher.form.lastName.placeholder')"
                  :error="$v.formFields.billingLastName.$error ? tr('errors.type.required') : ''"
                  extra-class="md:w-1/2"
                  variant="modern"
                  autocomplete="family-name"
                  @change=" updateDefaults()"
                />
              </div>

              <div class="flex flex-col space-y-4 md:flex-row md:justify-between md:space-y-0">
                <TextInput
                  v-model.trim="formFields.email"
                  name="email"
                  :label="tr('components.flightVoucher.form.email.name')"
                  :placeholder="tr('components.flightVoucher.form.email.placeholder')"
                  :error="$v.formFields.email.$error ? tr(`errors.type.${emailErrorType}`) : ''"
                  extra-class="w-full md:w-1/2 md:mr-10 xl:mr-6"
                  variant="modern"
                  autocomplete="email"
                  @change="updateDefaults()"
                />
                <TextInput
                  v-model.trim="formFields.emailConfirmation"
                  name="email-confirmation"
                  :label="tr('components.flightVoucher.form.emailConfirmation.name')"
                  :placeholder="tr('components.flightVoucher.form.emailConfirmation.placeholder')"
                  :error="$v.formFields.emailConfirmation.$error ? tr(`errors.type.${emailConfirmationErrorType}`) : ''"
                  extra-class="md:w-1/2"
                  variant="modern"
                  autocomplete="email"
                />
              </div>

              <div class="flex flex-col space-y-4 md:flex-row md:justify-between md:space-y-0">
                <TextInput
                  v-model.trim="formFields.street"
                  name="street"
                  :error="$v.formFields.street.$error ? tr('errors.type.required') : ''"
                  :label="tr('pages.sales.checkout.form.personal.street.name')"
                  :placeholder="tr('pages.sales.checkout.form.personal.street.placeholder')"
                  extra-class="w-full md:w-1/2 md:mr-10 xl:mr-6"
                  variant="modern"
                  autocomplete="address-line1"
                />
                <TextInput
                  v-model.trim="formFields.zipCode"
                  name="zipCode"
                  :error="$v.formFields.zipCode.$error ? tr(`errors.type.required`) : ''"
                  extra-class="w-full md:w-1/2"
                  :label="tr('pages.sales.checkout.form.personal.zip.name')"
                  :placeholder="tr('pages.sales.checkout.form.personal.zip.placeholder')"
                  variant="modern"
                  autocomplete="postal-code"
                />
              </div>

              <div class="flex flex-col space-y-4 md:flex-row md:justify-between md:space-y-0">
                <TextInput
                  v-model.trim="formFields.city"
                  name="city"
                  :error="$v.formFields.city.$error ? tr('errors.type.required') : ''"
                  :label="tr('pages.sales.checkout.form.personal.city.name')"
                  :placeholder="tr('pages.sales.checkout.form.personal.city.placeholder')"
                  extra-class="w-full md:w-1/2 md:mr-10 xl:mr-6"
                  variant="modern"
                  autocomplete="address-level2"
                />
                <TextInput
                  v-model="formFields.country"
                  name="country"
                  :error="$v.formFields.country.$error ? tr('errors.type.required') : ''"
                  :label="tr('pages.sales.checkout.form.personal.country.name')"
                  :placeholder="tr('pages.sales.checkout.form.personal.country.placeholder')"
                  extra-class="w-full md:w-1/2"
                  variant="modern"
                  autocomplete="country"
                />
              </div>
            </div>
          </div>

          <div v-show="hasErrors" ref="error">
            <Alert
              v-for="error in errors"
              :key="error"
              :title="tr(`errors.type.${error}.title`)"
              :message="tr(`errors.type.${error}.description`)"
            />
          </div>

          <div id="payment-datatrans-form" class="w-full p-6 bg-au_gray-100">
            <div class="pb-5 space-y-2 md:pb-7">
              <p class="font-bold md:text-2xl font-family-portado">
                {{ tr('pages.sales.checkout.form.payment.title') }}
              </p>

              <p class="text-sm leading-snug md:text-base">
                {{ tr('pages.sales.checkout.form.payment.description') }}
              </p>
            </div>

            <Card3dsInformationForm
              :locked="cardholderLocked"
              :default-name="formFields.cardholder.name || defaultCardholderName"
              :default-email="formFields.cardholder.email || defaultCardholderEmail"
              @unlock="onUnlock"
              @change="onChangeCardholder"
            />

            <div class="space-y-4 xl:space-y-6">
              <div v-show="cardholderLocked && secureFieldsReady" class="flex flex-col md:flex-row md:justify-between">
                <div
                  :class="{ 'is-active': secureFieldsStatus.cardNumber.focused || secureFieldsStatus.cardNumber.filled, 'is-error': secureFieldsStatus.cardNumber.error }"
                  class="w-full input-component-secure-field md:w-1/2 md:mr-10 xl:mr-6 relative"
                >
                  <label
                    v-if="shouldCardNumberDisplayLabel"
                    id="card-label"
                    :class="[
                      'text-xs cursor-text mb-2 flex pointer-events-none absolute top-1 left-4 z-10 text-au_gray-700' ,
                      shouldCardNumberDisplayError && 'text-au_error',
                      !shouldCardNumberDisplayError && secureFieldsStatus.cardNumber.focused && 'text-black',
                    ]"
                    for="card-number"
                  >
                    {{ tr('pages.sales.checkout.form.payment.cardNumber.name') }}
                  </label>
                  <div
                    class="relative input secure-field-input"
                    role="group"
                    aria-labelledby="card-label"
                    :class="{'is-focused': secureFieldsStatus.cardNumber.focused }"
                  >
                    <div id="card-number" />
                  </div>

                  <p v-if="shouldCardNumberDisplayError" class="error">
                    <span class="inline-flex px-2 py-1 mt-2 text-xs leading-normal text-au_error input-error bg-au_error-100">{{ tr(`errors.type.invalid`) }}</span>
                  </p>
                </div>

                <div class="flex justify-between mt-4 md:w-1/2 md:mt-0">
                  <TextInput
                    v-model="formFields.expiryDate"
                    v-mask="'##/##'"
                    :error="$v.formFields.expiryDate.$error ? tr(`errors.type.${expiryDateErrorType}`) : ''"
                    :label="tr('pages.sales.checkout.form.payment.expiryDate.name')"
                    :placeholder="tr('pages.sales.checkout.form.payment.expiryDate.placeholder')"
                    extra-class="w-8/12 mr-5"
                    autocomplete="cc-exp"
                    variant="modern"
                  />

                  <div
                    :class="{ 'is-active': secureFieldsStatus.cvv.focused || secureFieldsStatus.cvv.filled, 'is-error': shouldCvvDisplayError}"
                    class="w-full input-component-secure-field relative"
                  >
                    <label
                      v-if="shouldCvvDisplayLabel"
                      id="cvv-label"
                      :class="[
                        'text-xs cursor-text mb-2 flex pointer-events-none absolute top-1 left-4 z-10 text-au_gray-700' ,
                        shouldCvvDisplayError && 'text-au_error',
                        !shouldCvvDisplayError && secureFieldsStatus.cvv.focused && 'text-black',
                      ]"
                      for="cvv-number"
                    >
                      {{ tr('pages.sales.checkout.form.payment.cvv.name') }}
                    </label>
                    <div
                      class="relative input secure-field-input"
                      role="group"
                      aria-labelledby="cvv-label"
                      :class="{ 'is-focused': secureFieldsStatus.cvv.focused }"
                    >
                      <div id="cvv-number" />
                    </div>

                    <p v-if="shouldCvvDisplayError" class="error">
                      <span class="inline-flex px-2 py-1 mt-2 text-xs leading-normal text-au_error input-error bg-au_error-100">{{ tr('errors.type.invalid') }}</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="flex-shrink-0 w-full payment-summary xl:w-102 xl:flex">
          <ScrollWagon
            class="p-6 xl:w-102 bg-au_gray-100"
            top-limit-id="payment-personal-data"
            bottom-limit-id="payment-datatrans-form"
            :offset="120"
          >
            <VoucherAmountSummary class="pb-8 mb-10 border-b border-au_gray-400" :extended="true" />

            <div class="mb-6">
              <label class="flex cursor-pointer">
                <input
                  v-model="formFields.terms"
                  name="terms"
                  type="checkbox"
                  class="w-6 h-6 text-white border border-au_gray-400 form-checkbox form-checkbox-au"
                  :class="{ 'border-red is-error': $v.formFields.terms.$error }"
                >
                <i18n path="pages.sales.checkout.terms" class="ml-3 leading-tight pt-0.5" tag="p">
                  <template #link>
                    <router-link
                      class="underline"
                      :to="$i18nTo({ name: 'termsOfUse'})"
                      target="_blank"
                    >{{ tr('pages.sales.checkout.termsLink') }}</router-link>
                  </template>
                </i18n>
              </label>

              <i18n path="pages.sales.checkout.terms.privacyPolicy" class="ml-9" tag="p">
                <template #link>
                  <a
                    class="underline"
                    target="_blank"
                    :href="privacyLink"
                  >{{ tr('pages.sales.checkout.terms.privacyPolicyLink') }}</a>
                </template>
              </i18n>
            </div>

            <!-- Hidden until a legally approved promotion text is available (#4324)
            <div class="mb-6">
              <label class="flex cursor-pointer">
                <input
                  v-model="formFields.marketingConsent"
                  name="marketingConsent"
                  type="checkbox"
                  class="w-6 h-6 text-white border border-au_gray-400 form-checkbox form-checkbox-au"
                >
                <i18n path="pages.sales.checkout.marketingConsent" class="ml-3 leading-tight pt-0.5" tag="p">
                  <template #link>
                    <a
                      class="underline"
                      target="_blank"
                      :href="promotionTermsLink"
                    >{{ tr('pages.sales.checkout.marketingConsentLink') }}</a>
                  </template>
                </i18n>
              </label>
            </div>
            -->

            <div v-if="$v.formFields.terms.$error" class="mb-10 -mt-5 text-red">
              <span
                class="inline-flex px-2 py-1 mt-2 text-xs leading-normal text-au_error input-error bg-au_error-100"
              >
                {{ tr('pages.sales.checkout.termsPrivacyError') }}

              </span>
            </div>

            <div class="relative w-full">
              <Button
                variant="primary"
                :loading="isLoading"
                :disabled="!cardholderLocked || !secureFieldsReady"
                full-width
                type="submit"
              >
                {{ tr('pages.sales.checkout.button.purchase') }}
              </Button>
            </div>
          </ScrollWagon>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import VoucherSaleCheckout from '~brand-pages/base/voucher-sale-checkout'
import TextInput from '~brand-components/austrian/shared/text-input.vue'
import StepNavigation from '~brand-components/austrian/navigation/step-navigation.vue'
import Alert from '~brand-components/austrian/alert/alert.vue'
import Button from '~brand-components/austrian/button/button.vue'
import VoucherAmountSummary from '~brand-components/austrian/sale/voucher-amount-summary.vue'
import brandConfig from '~branding/austrian/config'
import Card3dsInformationForm from '~brand-components/austrian/sale/card-3ds-information-form.vue'

const SECURE_FIELDS_STYLES = {
  '*': `
    width: 100%;
    font-size: 1rem;
    color: #000000;
    background-color: white;
    height: 3rem;
    padding: 1rem 1rem 0;
    margin: 0;
    border-radius: 0.25rem;
    border: 1px solid transparent;
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    outline: 0;
  `,
  '*:focus': `
    border-color: #CAD1D9;
    padding-top: 1rem;
  `,
  '*::placeholder': `
    color: #394551;
  `,
  '*.valid': `
    border: 1px solid transparent;
  `,
  '*.empty': `
    border: 1px solid transparent;
    padding-top: 0;
  `,
  '*.empty:focus': `
    padding-top: 1rem;
  `,
  '*.empty:focus::placeholder': `
    color: transparent;
  `,
}

export default VoucherSaleCheckout.extend({
  components: {
    Card3dsInformationForm,
    TextInput,
    StepNavigation,
    Alert,
    Button,
    VoucherAmountSummary,
  },
  computed: {
    brandConfig() {
      return brandConfig
    },
    secureFieldStyles() {
      return SECURE_FIELDS_STYLES
    },
    privacyLink() {
      if (this.$i18n.locale === 'de') {
        return 'https://www.austrian.com/at/de/datenschutz'
      }

      return 'https://www.austrian.com/at/en/data-protection-information'
    },
    promotionTermsLink() {
      return `https://www.austrian.com/at/${this.$i18n.locale}/legal/voucher-conditions/austrian-voucher.html`
    },
  },
})
</script>

<style lang="scss" scoped>
.border-red {
  border-color: #8c1e33;
}

.input-component-secure-field {
  .input {
    height: 3rem;
  }
}

.secure-field-input {
  &.is-focused {
    position: relative;

    &::after {
      content: '';
      position: absolute;
      pointer-events: none;
      left: -6px;
      top: -6px;
      width: calc(100% + 12px);
      height: calc(100% + 12px);
      border: 1px solid black;
    }
  }
}
</style>
